import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Spinner, Button } from "react-bootstrap";
import { FaLocationArrow, FaExternalLinkAlt } from "react-icons/fa";
import {
  useJsApiLoader,
  GoogleMap,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";

const containerStyle = { width: "100%", height: "100%" };
const CONTROL_PANEL_WIDTH = "min(960px, 95%)";

function DeliveryRoute() {
  const { latitude, longitude } = useParams();
  const customerLat = parseFloat(latitude);
  const customerLng = parseFloat(longitude);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"], // keep places if you use it elsewhere
  });

  const [map, setMap] = useState(null);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [currentPosition, setCurrentPosition] = useState(null);
  const [loadingPos, setLoadingPos] = useState(true);
  const [routeError, setRouteError] = useState(null);
  const [tryingRoute, setTryingRoute] = useState(false);

  // get/browser location
  const getCurrentPosition = useCallback(() => {
    setLoadingPos(true);
    setRouteError(null);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentPosition({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLoadingPos(false);
        },
        (err) => {
          console.error("Geolocation error:", err);
          setLoadingPos(false);
          setRouteError(
            "Could not access your location. Please allow location access."
          );
        },
        { enableHighAccuracy: true, maximumAge: 15000, timeout: 10000 }
      );
    } else {
      setLoadingPos(false);
      setRouteError("Geolocation is not supported by your browser.");
    }
  }, []);

  useEffect(() => {
    getCurrentPosition();
    const id = setInterval(getCurrentPosition, 30000); // refresh periodically
    return () => clearInterval(id);
  }, [getCurrentPosition]);

  useEffect(() => {
    // calculate when we have currentPosition and map is ready
    if (currentPosition && isLoaded && map) {
      calculateRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition, isLoaded, map]);

  async function calculateRoute() {
    if (!currentPosition || !isLoaded || !window.google) return;
    setTryingRoute(true);
    setRouteError(null);
    setDirectionsResponse(null);
    setDistance("");
    setDuration("");

    try {
      const directionsService = new window.google.maps.DirectionsService();
      const results = await directionsService.route({
        origin: currentPosition,
        destination: { lat: customerLat, lng: customerLng },
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(),
        },
        // avoidTolls: false, // optional settings
      });

      // results is a DirectionsResult — check for routes
      if (results && results.routes && results.routes.length > 0) {
        setDirectionsResponse(results);
        const leg = results.routes[0].legs[0];
        setDistance(leg?.distance?.text ?? "");
        setDuration(leg?.duration?.text ?? "");
      } else {
        setRouteError("No route found.");
      }
    } catch (error) {
      // show the error and provide fallback
      console.error("Error fetching directions:", error);
      // Google often returns a MapsRequestError with details; capture that message
      const message =
        error?.message ||
        (error?.detail ? String(error.detail) : "Directions request failed.");
      setRouteError(message);
    } finally {
      setTryingRoute(false);
    }
  }

  // fallback URL for opening directions in Google Maps app / website
  const googleMapsDirectionsUrl = () => {
    const origin = currentPosition
      ? `${currentPosition.lat},${currentPosition.lng}`
      : "My+Location";
    const destination = `${customerLat},${customerLng}`;
    // use URL params documented here: https://developers.google.com/maps/documentation/urls/get-started
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      origin
    )}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
  };

  if (!isLoaded) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        height: "100vh",
        width: "100vw",
        background: "#f6f7f9",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "100%",
          width: "100%",
        }}
      >
        <GoogleMap
          center={currentPosition || { lat: customerLat, lng: customerLng }}
          zoom={15}
          mapContainerStyle={containerStyle}
          options={{
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            clickableIcons: false,
          }}
          onLoad={(m) => setMap(m)}
        >
          {currentPosition && <Marker position={currentPosition} label="You" />}
          <Marker
            position={{ lat: customerLat, lng: customerLng }}
            label="Customer"
          />
          {directionsResponse && (
            <DirectionsRenderer directions={directionsResponse} />
          )}
        </GoogleMap>
      </div>

      <div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: 20,
          zIndex: 2,
          width: CONTROL_PANEL_WIDTH,
        }}
      >
        <div className="p-3 bg-white shadow rounded">
          <Row className="align-items-center">
            <Col xs="auto">
              <div className="small text-muted">Distance</div>
              <div>
                <strong>{distance || "-"}</strong>
              </div>
            </Col>

            <Col xs="auto" className="ms-3">
              <div className="small text-muted">Duration</div>
              <div>
                <strong>{duration || "-"}</strong>
              </div>
            </Col>

            <Col className="text-end">
              <Button
                variant="light"
                className="me-2"
                title="Re-center map to current position"
                onClick={() => {
                  if (!map) return;
                  map.panTo(
                    currentPosition || { lat: customerLat, lng: customerLng }
                  );
                  map.setZoom(15);
                }}
              >
                <FaLocationArrow />
              </Button>

              <Button
                variant="primary"
                onClick={() => {
                  // if route failed or denied, open fallback
                  window.open(googleMapsDirectionsUrl(), "_blank");
                }}
              >
                Open in Google Maps <FaExternalLinkAlt className="ms-2" />
              </Button>
            </Col>
          </Row>

          {/* errors / status */}
          <Row className="mt-2">
            <Col>
              {loadingPos ? (
                <div className="text-muted small">
                  Fetching your location...
                </div>
              ) : tryingRoute ? (
                <div className="text-muted small">Calculating route...</div>
              ) : routeError ? (
                <div className="text-danger small">
                  Route error: {routeError}. You can try the button{" "}
                  <em>Open in Google Maps</em>.
                </div>
              ) : (
                <div className="text-success small">
                  Route calculated successfully.
                </div>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

export default DeliveryRoute;
